import React, { useState } from "react";
import { Box, Collapse, IconButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { OfferServiceRequest } from "../../models/models";
import { PublicServiceRequest } from "../../../serviceBoard/models/models";
import RequestDetailsPanel from "../steps/RequestDetailsPanel";
import { dt } from "../../../../shared/themes/redesignTokens";

interface OfferRfsSummaryProps {
  serviceRequest?: OfferServiceRequest;
  fullServiceRequest?: PublicServiceRequest;
  defaultExpanded?: boolean;
}

const OfferRfsSummary = ({
  serviceRequest,
  fullServiceRequest,
  defaultExpanded = false,
}: OfferRfsSummaryProps) => {
  const { translate } = useTranslation(["serviceOffers"]);
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (!serviceRequest) return null;

  const thumbnailUrl =
    serviceRequest.machine?.imageUrl ||
    serviceRequest.machine?.catalogImageUrl;

  return (
    <Box
      sx={{
        background: dt.cardBg,
        borderRadius: dt.radius,
        border: `1px solid ${dt.cardBorder}`,
        boxShadow: dt.cardShadow,
        mb: 2,
        transition: "box-shadow 0.2s ease",
        "&:hover": { boxShadow: dt.cardShadowHover },
      }}
    >
      {/* Compact header — always visible */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 1.5,
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Thumbnail */}
        {thumbnailUrl && (
          <Box
            component="img"
            src={thumbnailUrl}
            alt={serviceRequest.machine?.name || ""}
            sx={{
              width: 40,
              height: 40,
              objectFit: "cover",
              borderRadius: dt.radiusSm,
              border: `1px solid ${dt.cardBorder}`,
              flexShrink: 0,
            }}
          />
        )}

        {/* Machine name + brand + request number */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                color: dt.textPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {serviceRequest.machine?.name}
            </Box>
            {serviceRequest.machine?.brandName && (
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "12px",
                  color: dt.textSecondary,
                }}
              >
                {serviceRequest.machine.brandName}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "11px",
              color: dt.blue,
              fontWeight: 500,
            }}
          >
            {translate("offerForRequest", {
              number: serviceRequest.internalNumber,
            })}
          </Box>
        </Box>

        {/* Expand icon */}
        <IconButton size="small" sx={{ color: dt.textSecondary, flexShrink: 0 }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {/* Expanded content */}
      <Collapse in={expanded}>
        <Box
          sx={{
            px: 2,
            pb: 2,
            pt: 0.5,
            borderTop: `1px solid ${dt.cardBorder}`,
          }}
        >
          {fullServiceRequest ? (
            <RequestDetailsPanel serviceRequest={fullServiceRequest} />
          ) : (
            <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
              {thumbnailUrl && (
                <Box
                  component="img"
                  src={thumbnailUrl}
                  alt={serviceRequest.machine?.name || ""}
                  sx={{
                    width: 100,
                    height: 100,
                    objectFit: "cover",
                    borderRadius: dt.radiusSm,
                    border: `1px solid ${dt.cardBorder}`,
                  }}
                />
              )}
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "14px",
                    fontWeight: 600,
                    color: dt.textPrimary,
                  }}
                >
                  {serviceRequest.machine?.name}
                  {serviceRequest.machine?.brandName && (
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 400,
                        color: dt.textSecondary,
                        ml: 1,
                      }}
                    >
                      {serviceRequest.machine.brandName}
                    </Box>
                  )}
                </Box>
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "13px",
                    color: dt.textSecondary,
                    mt: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  {serviceRequest.issue}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default OfferRfsSummary;
